// frontend/src/hooks/useContractCatalogue.ts — the Contract surface's server
// state (charter · band families · tolerances · configurations ·
// commercial flags), all under `contractual_config:manage` + the has_invoicing
// entitlement (ADR-044 — an entitlement-off project 403s and the page's error
// arm renders it; the nav gate hides the entry anyway).
//
// Band families are NORMALIZED here ({ min, max } over the wire's min_kw/min_h)
// so one panel + one drawer serve all four families; the family's unit is
// display metadata (charterMeta), never a component fork. Each family list
// carries ITS tolerance (sparse row; absent = 0) — one query per panel. The
// normalized rows keep their stamps (ADR-084): a band's own, the tolerance's.

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { BandFamily } from '@ems-app/schemas/invoicing/charter';
import type {
  CommercialFlag,
  CommercialFlagListResponse,
  ContractualConfig,
  ContractualConfigListResponse,
  CreatedIdResponse,
  EffectiveCharterResponse,
  HoursBandListResponse,
  KwBandListResponse,
} from '@ems-app/schemas/invoicing/responses';
import type {
  CreateCommercialFlagRequest,
  CreateContractualConfigRequest,
  SetBandToleranceRequest,
  UpdateCommercialFlagRequest,
  UpdateContractualConfigRequest,
} from '@ems-app/schemas/invoicing/requests';
import { apiClient } from '../lib/apiClient.js';
import { refreshOnSettled } from '../lib/settled.js';

// ── keys ─────────────────────────────────────────────────────────────────────
export const contractKeys = {
  charter: (projectId: string) => ['contract-charter', projectId] as const,
  bands: (projectId: string, family: BandFamily) => ['contract-bands', projectId, family] as const,
  configs: (projectId: string) => ['contract-configs', projectId] as const,
  flags: (projectId: string) => ['commercial-flags', projectId] as const,
};

const FAMILY_PATH: Record<BandFamily, string> = {
  load: 'load-bands',
  grid_availability: 'grid-availability-bands',
  solar: 'solar-bands',
  cooling: 'cooling-bands',
};

// ── charter ──────────────────────────────────────────────────────────────────
export function useCharter(projectId: string): UseQueryResult<EffectiveCharterResponse, Error> {
  return useQuery({
    queryKey: contractKeys.charter(projectId),
    queryFn: async () => {
      const r = await apiClient.get<EffectiveCharterResponse>(`/projects/${projectId}/catalogue-charter`);
      return r.data;
    },
    enabled: projectId.length > 0,
  });
}

/** The Terms drawers' single Save — one batch PATCH, invalidated as one.
 *  The invalidation promise is RETURNED so mutateAsync settles only after the
 *  charter refetch lands — a drawer reopened right after a save reseeds from
 *  fresh natures, never from the stale pre-save cache. */
export function useSetCharterFields(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (changes: ReadonlyArray<{ field: string; nature: string }>) => {
      await apiClient.patch(`/projects/${projectId}/catalogue-charter`, { changes });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: contractKeys.charter(projectId) }),
  });
}

// ── band families (normalized) ───────────────────────────────────────────────
export interface Band {
  id: number;
  name: string;
  min: number;
  max: number;
  is_disabled: boolean;
  row_version: number;
}

export interface BandFamilyData {
  items: Band[];
  tolerance: number;
  /** The tolerance row's own stamp — null while the family has none (its first save inserts it). */
  tolerance_row_version: number | null;
}

// One normalized read for the four families; the hours family differs only in
// its wire keys. Selectors are per-family module constants (referentially
// stable, §State select doctrine).
const normalizeKw = (r: KwBandListResponse): BandFamilyData => ({
  items: r.items.map((b) => ({ id: b.id, name: b.name, min: b.min_kw, max: b.max_kw, is_disabled: b.is_disabled, row_version: b.row_version })),
  tolerance: r.tolerance,
  tolerance_row_version: r.tolerance_row_version,
});
const normalizeHours = (r: HoursBandListResponse): BandFamilyData => ({
  items: r.items.map((b) => ({ id: b.id, name: b.name, min: b.min_h, max: b.max_h, is_disabled: b.is_disabled, row_version: b.row_version })),
  tolerance: r.tolerance,
  tolerance_row_version: r.tolerance_row_version,
});

/** The family's list read, normalized — the panel's, and a form's Load the latest (a band has no detail read: the
 *  list row is its read; the entry holds the normalized shape). */
export async function fetchBandFamily(projectId: string, family: BandFamily): Promise<BandFamilyData> {
  if (family === 'grid_availability') {
    const r = await apiClient.get<HoursBandListResponse>(`/projects/${projectId}/${FAMILY_PATH[family]}`);
    return normalizeHours(r.data);
  }
  const r = await apiClient.get<KwBandListResponse>(`/projects/${projectId}/${FAMILY_PATH[family]}`);
  return normalizeKw(r.data);
}

export function useBandFamily(projectId: string, family: BandFamily): UseQueryResult<BandFamilyData, Error> {
  return useQuery({
    queryKey: contractKeys.bands(projectId, family),
    queryFn: () => fetchBandFamily(projectId, family),
    enabled: projectId.length > 0,
  });
}

/** Map the normalized {min,max} onto the family's wire keys (the stamp rides as it is). */
function bandBody(family: BandFamily, v: { row_version?: number; name?: string; min?: number; max?: number }) {
  const unitKeys = family === 'grid_availability' ? (['min_h', 'max_h'] as const) : (['min_kw', 'max_kw'] as const);
  const body: Record<string, unknown> = {};
  if (v.row_version !== undefined) body.row_version = v.row_version;
  if (v.name !== undefined) body.name = v.name;
  if (v.min !== undefined) body[unitKeys[0]] = v.min;
  if (v.max !== undefined) body[unitKeys[1]] = v.max;
  return body;
}

export function useCreateBand(projectId: string, family: BandFamily) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { name: string; min: number; max: number }) => {
      const r = await apiClient.post<CreatedIdResponse>(`/projects/${projectId}/${FAMILY_PATH[family]}`, bandBody(family, v));
      return r.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: contractKeys.bands(projectId, family) }),
  });
}

export function useUpdateBand(projectId: string, family: BandFamily) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { bandId: number; row_version?: number; name?: string; min?: number; max?: number }) => {
      await apiClient.patch(`/projects/${projectId}/${FAMILY_PATH[family]}/${v.bandId}`, bandBody(family, v));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: contractKeys.bands(projectId, family) }),
  });
}

export function useDeleteBand(projectId: string, family: BandFamily) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bandId: number) => {
      await apiClient.delete(`/projects/${projectId}/${FAMILY_PATH[family]}/${bandId}`);
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: contractKeys.bands(projectId, family) })),
  });
}

export function useSetBandDisabled(projectId: string, family: BandFamily) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { bandId: number; disabled: boolean }) => {
      await apiClient.post(`/projects/${projectId}/${FAMILY_PATH[family]}/${v.bandId}/${v.disabled ? 'disable' : 'reactivate'}`);
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: contractKeys.bands(projectId, family) })),
  });
}

export function useSetBandTolerance(projectId: string, family: BandFamily) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetBandToleranceRequest) => {
      await apiClient.put(`/projects/${projectId}/band-tolerances/${family}`, body);
    },
    // The tolerance rides the family's band list response.
    onSuccess: () => void qc.invalidateQueries({ queryKey: contractKeys.bands(projectId, family) }),
  });
}

// ── contractual configurations ──────────────────────────────────────────────
// One list fetch with disabled rows included; status/search/completeness narrow
// client-side (tens of rows — the recorded no-pagination decision).
const selectConfigs = (r: ContractualConfigListResponse): ContractualConfig[] => r.items;

/** The list read — the page's, and a form's Load the latest (the list row is the configuration's read). */
export async function fetchConfigs(projectId: string): Promise<ContractualConfigListResponse> {
  const r = await apiClient.get<ContractualConfigListResponse>(`/projects/${projectId}/contractual-configurations`, { params: { include_disabled: 'true' } });
  return r.data;
}

export function useContractConfigs(projectId: string): UseQueryResult<ContractualConfig[], Error> {
  return useQuery({
    queryKey: contractKeys.configs(projectId),
    queryFn: () => fetchConfigs(projectId),
    enabled: projectId.length > 0,
    select: selectConfigs,
  });
}

export function useCreateConfig(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateContractualConfigRequest) => {
      const r = await apiClient.post<CreatedIdResponse>(`/projects/${projectId}/contractual-configurations`, body);
      return r.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: contractKeys.configs(projectId) }),
  });
}

export function useUpdateConfig(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { configId: number; body: UpdateContractualConfigRequest }) => {
      await apiClient.patch(`/projects/${projectId}/contractual-configurations/${v.configId}`, v.body);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: contractKeys.configs(projectId) }),
  });
}

export function useDeleteConfig(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (configId: number) => {
      await apiClient.delete(`/projects/${projectId}/contractual-configurations/${configId}`);
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: contractKeys.configs(projectId) })),
  });
}

export function useSetConfigDisabled(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { configId: number; disabled: boolean }) => {
      await apiClient.post(`/projects/${projectId}/contractual-configurations/${v.configId}/${v.disabled ? 'disable' : 'reactivate'}`);
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: contractKeys.configs(projectId) })),
  });
}

// ── commercial flags ─────────────────────────────────────────────────────────
const selectFlags = (r: CommercialFlagListResponse): CommercialFlag[] => r.items;

/** The list read — the page's, and a form's Load the latest (a flag has no detail read: the list row is its read). */
export async function fetchFlags(projectId: string): Promise<CommercialFlagListResponse> {
  const r = await apiClient.get<CommercialFlagListResponse>(`/projects/${projectId}/commercial-flags`);
  return r.data;
}

export function useCommercialFlags(projectId: string): UseQueryResult<CommercialFlag[], Error> {
  return useQuery({
    queryKey: contractKeys.flags(projectId),
    queryFn: () => fetchFlags(projectId),
    enabled: projectId.length > 0,
    select: selectFlags,
  });
}

export function useCreateFlag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCommercialFlagRequest) => {
      const r = await apiClient.post<CreatedIdResponse>(`/projects/${projectId}/commercial-flags`, body);
      return r.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: contractKeys.flags(projectId) }),
  });
}

export function useUpdateFlag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { flagId: number; body: UpdateCommercialFlagRequest }) => {
      await apiClient.patch(`/projects/${projectId}/commercial-flags/${v.flagId}`, v.body);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: contractKeys.flags(projectId) }),
  });
}

export function useDeleteFlag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flagId: number) => {
      await apiClient.delete(`/projects/${projectId}/commercial-flags/${flagId}`);
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: contractKeys.flags(projectId) })),
  });
}

export function useSetFlagDisabled(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { flagId: number; disabled: boolean }) => {
      await apiClient.post(`/projects/${projectId}/commercial-flags/${v.flagId}/${v.disabled ? 'disable' : 'reactivate'}`);
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: contractKeys.flags(projectId) })),
  });
}
